import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ReceiptText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { featureAllowed } from "@/lib/access";
import { usePOS } from "@/context/POSContext";
import { inr, elapsedFromSeconds } from "@/lib/format";
import { EmptyState, LoadingState } from "@/components/ui/StateBoundaries";
import { NetworkIndicator } from "@/components/captain/NetworkIndicator";
import { useCaptainNetwork } from "@/hooks/useCaptainNetwork";
import { listOrders } from "@/lib/orderApi";
import { meshTablesPath } from "@/lib/captainMesh/routes";

/**
 * Pending Bills (bible §39.2): bills printed but not yet settled. The table was
 * released the moment the bill printed; the money still has to arrive, and this
 * queue is where it does.
 *
 * Same visual language as the floor: square tiles, one per pending bill, table
 * number up top and the amount underneath. Tapping a tile opens the bill
 * preview (/captain/bill/:orderId) where Print Duplicate — and, for staff
 * holding billing.settle, Settle — live.
 */
export default function CaptainPendingBills() {
  const navigate = useNavigate();
  const { token, tables, user, permissions } = usePOS();
  const { isOnline } = useCaptainNetwork();
  // A per-person switch (2026-10-20); the server refuses the list without it too.
  const allowed = featureAllowed("orders.pending_bills", user, permissions);

  const [state, setState] = useState({ phase: "loading", bills: [] });

  const refresh = useCallback(async () => {
    if (!allowed) {
      setState({ phase: "denied", bills: [] });
      return;
    }
    if (!token || !isOnline) {
      setState({ phase: "offline", bills: [] });
      return;
    }
    try {
      const bills = await listOrders({ status: "billing" }, token);
      setState({ phase: "ready", bills });
    } catch {
      setState({ phase: "error", bills: [] });
    }
  }, [token, isOnline, allowed]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, [refresh]);

  return (
    <div className="flex flex-col h-full bg-background" data-testid="captain-pending-bills-screen">
      <header className="min-h-[3.5rem] pt-safe flex items-center justify-between px-3 bg-primary text-primary-foreground shadow-md shrink-0">
        <div className="flex items-center gap-2">
          <button
            className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full hover:bg-primary-foreground/20"
            aria-label="Back to tables"
            onClick={() => navigate(meshTablesPath())}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex flex-col">
            <small className="text-micro font-bold opacity-80 uppercase">Awaiting settlement</small>
            <h2 className="text-body font-bold leading-tight">Pending bills</h2>
          </div>
        </div>
        <NetworkIndicator />
      </header>

      <div className="px-base pt-snug text-micro font-bold uppercase text-muted-foreground/70 shrink-0">
        Tap a bill to preview, reprint or settle it
      </div>

      <div className="flex-1 overflow-y-auto p-base">
        {state.phase === "loading" && <LoadingState message="Reading pending bills…" />}
        {state.phase === "offline" && (
          <EmptyState title="Offline" message="Pending bills live on the server — reconnect to see them." />
        )}
        {state.phase === "denied" && (
          <EmptyState title="Not enabled for you" message="Pending bills is not enabled for your account. An admin can allow it in Staff." />
        )}
        {state.phase === "error" && (
          <EmptyState title="Could not load" message="The server did not answer. Go back and retry." />
        )}
        {state.phase === "ready" && !state.bills.length && (
          <EmptyState title="Nothing pending" message="Every printed bill has been settled." />
        )}

        {state.phase === "ready" && state.bills.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="pending-bill-grid">
            {state.bills.map(bill => (
              <button
                key={bill.id}
                className="w-full aspect-square relative flex flex-col items-center justify-center px-2 py-2 rounded-lg border-2 border-warning bg-warning-subtle text-foreground shadow-sm transition-transform active:scale-95"
                onClick={() => navigate(`/captain/bill/${bill.id}`)}
                data-testid={`pending-bill-${bill.order_number}`}
              >
                <div className="absolute top-1.5 left-1.5 w-2 h-2 rounded-full bg-warning" />
                <ReceiptText className="w-4 h-4 mb-0.5 opacity-60" />
                <strong className="text-body font-bold leading-tight">
                  {bill.table_number != null ? (tables?.find(t => t.number == bill.table_number)?.name || `Table ${bill.table_number}`) : "Parcel"}
                </strong>
                <span className="text-micro font-bold opacity-70 tabular-nums">#{bill.order_number}</span>
                <span className="mt-0.5 text-body font-bold tabular-nums">{inr(bill.amount)}</span>
                <small className="text-micro uppercase font-bold leading-tight text-warning">
                  {elapsedFromSeconds(bill.elapsed_seconds)}
                </small>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
